import axios from "axios";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "./constants";
import { getUserTimezone } from "./utils/timezone";

// Base por defecto relativa: se adapta automáticamente a http/https, IP o dominio.
// VITE_API_URL (si se define) debe apuntar al host y (preferentemente) terminar en /api o /api/.
// Si el valor proporcionado no contiene "/api" al final, lo forzamos para evitar llamadas a rutas sin prefijo que dan 405 HTML.
const DEFAULT_BASE = "/api/";
let computedBase;
// Override de desarrollador vía localStorage (opcional):
// localStorage.setItem('force_api_base', 'local'|'remote'|'custom')
// localStorage.setItem('force_api_url', 'http://IP:PUERTO/api') cuando 'custom'
let devOverrideMode = null;
let devOverrideUrl = null;
try {
  if (typeof window !== 'undefined' && window.localStorage) {
    devOverrideMode = window.localStorage.getItem('force_api_base');
    devOverrideUrl = window.localStorage.getItem('force_api_url');
  }
} catch {}
// En desarrollo, forzar base relativa salvo que el usuario pida 'custom'.
// Esto garantiza que funcione igual abriendo por localhost o por IP LAN usando el proxy de Vite.
if (import.meta && import.meta.env && import.meta.env.DEV && devOverrideMode !== 'custom') {
  computedBase = DEFAULT_BASE;
} else if (devOverrideMode === 'local') {
  computedBase = DEFAULT_BASE;
} else if (devOverrideMode === 'custom' && devOverrideUrl) {
  let raw = String(devOverrideUrl).trim();
  raw = raw.replace(/\s+/g, "").replace(/\/+$/, "");
  computedBase = /\/api$/i.test(raw) ? raw + '/' : raw + '/api/';
} else if (import.meta.env.VITE_API_URL) {
  let raw = String(import.meta.env.VITE_API_URL).trim();
  // Quitar espacios y trailing slashes redundantes
  raw = raw.replace(/\s+/g, "").replace(/\/+$/, "");
  const lower = raw.toLowerCase();
  // Detectar si ya contiene un sufijo /api o /api/ explícito
  if (/(^|\/ )api(\/?$)/i.test(lower) || /\/api$/i.test(lower) || /\/api\/$/i.test(lower) || /\/api\?/i.test(lower)) {
    // Normalizar a terminar en /
    computedBase = raw.replace(/\/$/, "") + "/";
  } else {
    // Añadir /api/
    computedBase = raw + "/api/";
  }
} else {
  computedBase = DEFAULT_BASE;
}

if (typeof window !== 'undefined') {
  try {
    if (devOverrideMode) {
      console.info('[api] Override dev activo:', devOverrideMode, devOverrideMode==='custom'?devOverrideUrl:'' );
    }
    // Si la base apunta a localhost/127.0.0.1 pero el sitio se abre por IP LAN/domino, usar proxy relativo /api/
    if (/^https?:\/\//i.test(computedBase)) {
      const parsed = new URL(computedBase, window.location.origin);
      const isLocalBase = parsed.hostname === '127.0.0.1' || parsed.hostname === 'localhost';
      const isLocalHost = window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost';
      if (isLocalBase && !isLocalHost) {
        console.warn('[api] VITE_API_URL apunta a', parsed.hostname, 'pero el sitio corre en', window.location.hostname, '→ usando base relativa /api/');
        computedBase = DEFAULT_BASE;
      }
      // Heurística adicional: en desarrollo desde IP privada (LAN) y base absoluta a otro host, forzar proxy relativo
      const port = String(window.location.port || '');
      const isDevPort = port === '5173';
      const host = window.location.hostname;
      const isPrivateIP = /^10\./.test(host) || /^192\.168\./.test(host) || /^172\.(1[6-9]|2\d|3[0-1])\./.test(host);
      const isDifferentHost = parsed.hostname !== host;
      if (isDevPort && isPrivateIP && isDifferentHost) {
        console.warn('[api] Dev por IP privada', host, 'con base absoluta a', parsed.hostname, '→ usando base relativa /api/');
        computedBase = DEFAULT_BASE;
      }
    }
  } catch {}
  try {
    // Aviso diagnóstico (solo en consola del navegador) si la base final NO termina en /api/
    if (!/\/api\/$/i.test(computedBase)) {
      console.warn('[api] baseURL no termina en /api/ (' + computedBase + '). Verifica configuración.');
    }
    console.info('[api] baseURL en uso:', computedBase);
  } catch {}
}

// Exportar la base efectiva para mostrarla en la UI (útil en desarrollo)
export const API_BASE = computedBase;

const api = axios.create({ baseURL: computedBase, timeout: 20000 });

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ACCESS_TOKEN);
    const timezone = getUserTimezone();
    const url = (config?.url || '').replace(/^\//, '');
    // Evitar adjuntar Authorization en endpoints de auth para prevenir preflight innecesario
    const isAuthEndpoint = url === 'token/' || url === 'token/refresh/' || url.startsWith('token?') || url.startsWith('token/refresh?');
    if (token && !isAuthEndpoint) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers = config.headers || {};
    config.headers['X-WS-Timezone'] = timezone;
    return config;
  },
  (error) => Promise.reject(error)
);

export default api;

// Interceptor de respuesta: intentar refrescar en 401 y reintentar una vez
let isRefreshing = false;
let pendingRequests = [];

const onRefreshed = (newToken) => {
  pendingRequests.forEach((cb) => cb(newToken));
  pendingRequests = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error?.response?.status;

    // Evitar bucle infinito y solo actuar en 401
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refresh = localStorage.getItem(REFRESH_TOKEN);
      if (!refresh) {
        // No hay refresh: limpiar y rechazar
        localStorage.removeItem(ACCESS_TOKEN);
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Poner en cola hasta que termine el refresh en curso
        return new Promise((resolve, reject) => {
          pendingRequests.push((newToken) => {
            if (!newToken) return reject(error);
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;
      try {
        const { data } = await api.post("token/refresh/", { refresh });
        const newAccess = data?.access;
        if (newAccess) {
          localStorage.setItem(ACCESS_TOKEN, newAccess);
          api.defaults.headers.common["Authorization"] = `Bearer ${newAccess}`;
          onRefreshed(newAccess);
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccess}`;
          // Avisar a otros módulos (SSE) que el token cambió
          try { window.dispatchEvent(new CustomEvent('auth:token_refreshed', { detail: { access: newAccess } })); } catch(_) {}
          return api(originalRequest);
        }
      } catch (e) {
        // Refresh inválido: limpiar tokens
        localStorage.removeItem(ACCESS_TOKEN);
        localStorage.removeItem(REFRESH_TOKEN);
        try { window.dispatchEvent(new CustomEvent('auth:logout')); } catch(_) {}
        // Redirigir a login si estamos en una vista protegida
        try { if (window.location && !/\/login$/i.test(window.location.pathname)) { setTimeout(() => { window.location.href = '/login'; }, 50); } } catch(_) {}
      } finally {
        isRefreshing = false;
        onRefreshed(null);
      }
    }
    return Promise.reject(error);
  }
);
